import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import {
  Modal,
  Tabs,
  Box,
  Group,
  Stack,
  Text,
  Button,
  Table,
  Loader,
  Alert,
  Badge,
  Select,
  TextInput,
  Pagination,
  ScrollArea,
  Tooltip,
} from '@mantine/core';
import {
  IconHistory,
  IconRefresh,
  IconAlertTriangle,
  IconLogin,
  IconListDetails,
} from '@tabler/icons-react';
import {
  ACTIONS,
  actionLabel,
  actionColor,
  actorName,
  describeDetail,
  formatActivityTime,
  localeLabel,
  roleLabel,
  surfaceLabel,
  methodLabel,
} from '../tools/activityLogFormat.js';

/**
 * SchoolActivityDialog — what a SCHOOL ADMIN (user.role >= 2) sees of their
 * own school: the staff roster with sign-in / edit counts, every staff
 * sign-in, and every create / update / delete / change-owner on the school's
 * locales (ev_web_v2 /api/activity/users + /api/activity/list, table
 * `epack_activity_log`).
 *
 * Scope is the SERVER's: both routes ignore a role-2 caller's school_id and
 * answer for the token's school. This dialog sends it only so a platform
 * admin (role >= 3) gets their own school by default too.
 *
 * Three tabs, one list request per tab: the login tab asks for
 * action=login, the locale tab for entity_type=locale (+ an optional action).
 * Paged server-side (PAGE_SIZE) — a school's history is unbounded.
 */
const PAGE_SIZE = 50;
const TAB_TEACHERS = 'teachers';
const TAB_LOGINS = 'logins';
const TAB_LOCALES = 'locales';

const LOCALE_ACTIONS = ACTIONS.filter((a) => a !== 'login');

class SchoolActivityDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: TAB_TEACHERS,
      users: [],
      usersLoading: false,
      usersFailed: false,
      rows: [],
      total: 0,
      page: 1,
      loading: false,
      loadFailed: false,
      filterUserId: null,
      filterAction: null,
      dateFrom: '',
      dateTo: '',
    };
    // Guards a late response from an earlier tab/filter overwriting the
    // current one: only the newest request may write `rows`.
    this.requestSeq = 0;
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({
        tab: TAB_TEACHERS, rows: [], total: 0, page: 1,
        filterUserId: null, filterAction: null, dateFrom: '', dateTo: '',
      }, this.fetchUsers);
    }
  }

  api() {
    const root = this.props.root;
    return root && root.refs && root.refs.API ? root.refs.API : null;
  }

  schoolId() {
    const root = this.props.root;
    return root && root.getMySchoolId ? root.getMySchoolId() : null;
  }

  // ------------------------------------------------------------ teachers tab

  fetchUsers() {
    const api = this.api();
    if (!api) {
      return;
    }
    this.setState({ usersLoading: true, usersFailed: false });
    api.request("get_activity_users", { school_id: this.schoolId() }, this.fetchUsersOK, this.fetchUsersFailed);
  }

  fetchUsersOK(res) {
    const rows = res && res.body && Array.isArray(res.body.result) ? res.body.result : [];
    this.setState({ usersLoading: false, users: rows });
  }

  fetchUsersFailed(err) {
    console.log("get_activity_users failed", err);
    this.setState({ usersLoading: false, usersFailed: true, users: [] });
  }

  // --------------------------------------------------------------- list tabs

  fetchRows(page) {
    const api = this.api();
    if (!api || this.state.tab === TAB_TEACHERS) {
      return;
    }
    const seq = ++this.requestSeq;
    const targetPage = page || this.state.page || 1;
    const req = {
      school_id: this.schoolId(),
      user_id: this.state.filterUserId ? parseInt(this.state.filterUserId, 10) : undefined,
      date_from: this.state.dateFrom || undefined,
      date_to: this.state.dateTo || undefined,
      limit: PAGE_SIZE,
      offset: (targetPage - 1) * PAGE_SIZE,
    };
    if (this.state.tab === TAB_LOGINS) {
      req.action = 'login';
    } else {
      req.entity_type = 'locale';
      if (this.state.filterAction) {
        req.action = this.state.filterAction;
      }
    }
    this.setState({ loading: true, loadFailed: false, page: targetPage });
    api.request(
      "get_activity_list", req,
      (res) => this.fetchRowsOK(res, seq),
      (err) => this.fetchRowsFailed(err, seq)
    );
  }

  fetchRowsOK(res, seq) {
    if (seq !== this.requestSeq) {
      return;
    }
    const body = res && res.body ? res.body : {};
    this.setState({
      loading: false,
      rows: Array.isArray(body.result) ? body.result : [],
      total: typeof body.total === 'number' ? body.total : 0,
    });
  }

  fetchRowsFailed(err, seq) {
    if (seq !== this.requestSeq) {
      return;
    }
    console.log("get_activity_list failed", err);
    this.setState({ loading: false, loadFailed: true, rows: [], total: 0 });
  }

  handleTabChange(tab) {
    if (!tab || tab === this.state.tab) {
      return;
    }
    this.setState({ tab: tab, rows: [], total: 0, page: 1, filterAction: null }, () => {
      if (tab === TAB_TEACHERS) {
        this.fetchUsers();
      } else {
        this.fetchRows(1);
      }
    });
  }

  /** From the teachers table: jump to a list tab already filtered to one person. */
  showUserActivity(userId, tab) {
    this.setState({ tab: tab, filterUserId: String(userId), filterAction: null, rows: [], total: 0, page: 1 },
      () => this.fetchRows(1));
  }

  setFilter(patch) {
    this.setState(patch, () => this.fetchRows(1));
  }

  // ------------------------------------------------------------------ render

  userOptions() {
    const s = Localization.strings;
    const opts = this.state.users.map((u) => ({
      value: String(u.id),
      label: (u.display_name ? u.display_name + ' (' + u.username + ')' : u.username) || ('#' + u.id),
    }));
    // The filter may point at someone no longer on the roster (deleted
    // account): keep the option so the Select does not silently drop it.
    if (this.state.filterUserId && !opts.some((o) => o.value === this.state.filterUserId)) {
      opts.push({ value: this.state.filterUserId, label: '#' + this.state.filterUserId });
    }
    return [{ value: '', label: s.ACTIVITY_FILTER_ALL || 'All' }].concat(opts);
  }

  renderFilters(withAction) {
    const s = Localization.strings;
    return (
      <Group gap="sm" align="flex-end" wrap="wrap" mb="sm">
        <Select
          label={s.ACTIVITY_FILTER_TEACHER || 'Teacher'}
          data={this.userOptions()}
          value={this.state.filterUserId || ''}
          onChange={(v) => this.setFilter({ filterUserId: v || null })}
          searchable
          w={260}
          comboboxProps={{ zIndex: 2200 }}
        />
        {withAction ? (
          <Select
            label={s.ACTIVITY_FILTER_ACTION || 'Action'}
            data={[{ value: '', label: s.ACTIVITY_FILTER_ALL || 'All' }]
              .concat(LOCALE_ACTIONS.map((a) => ({ value: a, label: actionLabel(s, a) })))}
            value={this.state.filterAction || ''}
            onChange={(v) => this.setFilter({ filterAction: v || null })}
            w={180}
            comboboxProps={{ zIndex: 2200 }}
          />
        ) : null}
        <TextInput
          type="date"
          label={s.ACTIVITY_FILTER_FROM || 'From'}
          value={this.state.dateFrom}
          onChange={(e) => this.setFilter({ dateFrom: e.currentTarget.value })}
          w={160}
        />
        <TextInput
          type="date"
          label={s.ACTIVITY_FILTER_TO || 'To'}
          value={this.state.dateTo}
          onChange={(e) => this.setFilter({ dateTo: e.currentTarget.value })}
          w={160}
        />
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={() => this.fetchRows(this.state.page)}>
          {s.ACTIVITY_REFRESH || 'Refresh'}
        </Button>
        <Text size="sm" c="dimmed" ml="auto">
          {String(s.ACTIVITY_TOTAL || '{0} record(s)').replace('{0}', String(this.state.total))}
        </Text>
      </Group>
    );
  }

  renderPagination() {
    const pages = Math.max(1, Math.ceil(this.state.total / PAGE_SIZE));
    if (pages <= 1) {
      return null;
    }
    return (
      <Group justify="center" mt="sm">
        <Pagination total={pages} value={this.state.page} onChange={(p) => this.fetchRows(p)} size="sm" />
      </Group>
    );
  }

  renderState(emptyWhen) {
    const s = Localization.strings;
    if (this.state.loading) {
      return <Group justify="center" p="lg"><Loader size="sm" /></Group>;
    }
    if (this.state.loadFailed) {
      return (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mt="sm">
          {s.ACTIVITY_LOAD_FAILED || 'Could not load the activity log'}
        </Alert>
      );
    }
    if (emptyWhen) {
      return <Text c="dimmed" size="sm" p="md" ta="center">{s.ACTIVITY_EMPTY || 'No records'}</Text>;
    }
    return null;
  }

  renderTeachers() {
    const s = Localization.strings;
    if (this.state.usersLoading) {
      return <Group justify="center" p="lg"><Loader size="sm" /></Group>;
    }
    if (this.state.usersFailed) {
      return (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mt="sm">
          {s.ACTIVITY_LOAD_FAILED || 'Could not load the activity log'}
        </Alert>
      );
    }
    if (!this.state.users.length) {
      return <Text c="dimmed" size="sm" p="md" ta="center">{s.ACTIVITY_EMPTY || 'No records'}</Text>;
    }
    return (
      <Table striped highlightOnHover withTableBorder fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{s.ACTIVITY_COL_TEACHER || 'Teacher'}</Table.Th>
            <Table.Th>{s.USERNAME || 'Username'}</Table.Th>
            <Table.Th>{s.EMAIL || 'Email'}</Table.Th>
            <Table.Th>{s.ACTIVITY_COL_ROLE || 'Role'}</Table.Th>
            <Table.Th>{s.ACTIVITY_COL_LAST_LOGIN || 'Last sign-in'}</Table.Th>
            <Table.Th ta="right">{s.ACTIVITY_COL_LOGIN_COUNT || 'Sign-ins'}</Table.Th>
            <Table.Th ta="right">{s.ACTIVITY_COL_ACTION_COUNT || 'LOCALE actions'}</Table.Th>
            <Table.Th>{s.ACTIVITY_COL_LAST_ACTION || 'Last LOCALE action'}</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {this.state.users.map((u) => (
            <Table.Tr key={u.id}>
              <Table.Td>{u.display_name || '-'}</Table.Td>
              <Table.Td>{u.username}</Table.Td>
              <Table.Td>{u.email || '-'}</Table.Td>
              <Table.Td>{roleLabel(s, u.role)}</Table.Td>
              <Table.Td>{formatActivityTime(u.last_login_time) || (s.ACTIVITY_NEVER || 'Never')}</Table.Td>
              <Table.Td ta="right">{u.login_count}</Table.Td>
              <Table.Td ta="right">{u.locale_action_count}</Table.Td>
              <Table.Td>{formatActivityTime(u.last_locale_action_at) || '-'}</Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label={s.ACTIVITY_VIEW_LOGINS || 'Sign-ins'}>
                    <Button size="compact-xs" variant="light" leftSection={<IconLogin size={14} />}
                      onClick={() => this.showUserActivity(u.id, TAB_LOGINS)}>
                      {s.ACTIVITY_VIEW_LOGINS || 'Sign-ins'}
                    </Button>
                  </Tooltip>
                  <Tooltip label={s.ACTIVITY_VIEW_ACTIONS || 'Actions'}>
                    <Button size="compact-xs" variant="light" leftSection={<IconListDetails size={14} />}
                      onClick={() => this.showUserActivity(u.id, TAB_LOCALES)}>
                      {s.ACTIVITY_VIEW_ACTIONS || 'Actions'}
                    </Button>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  renderLogins() {
    const s = Localization.strings;
    const state = this.renderState(!this.state.rows.length);
    return (
      <Box>
        {this.renderFilters(false)}
        {state || (
          <Table striped highlightOnHover withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{s.ACTIVITY_COL_TIME || 'Time'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_TEACHER || 'Teacher'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_METHOD || 'Sign-in method'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_SURFACE || 'Where'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_IP || 'IP'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {this.state.rows.map((r) => {
                const d = r.detail && typeof r.detail === 'object' ? r.detail : {};
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatActivityTime(r.created_at)}</Table.Td>
                    <Table.Td>{actorName(r)}</Table.Td>
                    <Table.Td>{methodLabel(s, d.method)}</Table.Td>
                    <Table.Td>{surfaceLabel(s, d.surface)}</Table.Td>
                    <Table.Td>
                      <Tooltip label={r.user_agent || '-'} multiline w={320} disabled={!r.user_agent}>
                        <span>{r.ip || '-'}</span>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
        {this.renderPagination()}
      </Box>
    );
  }

  renderLocales() {
    const s = Localization.strings;
    const state = this.renderState(!this.state.rows.length);
    return (
      <Box>
        {this.renderFilters(true)}
        {state || (
          <Table striped highlightOnHover withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{s.ACTIVITY_COL_TIME || 'Time'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_TEACHER || 'Teacher'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_ACTION || 'Action'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_LOCALE || 'LOCALE'}</Table.Th>
                <Table.Th>{s.ACTIVITY_COL_DETAIL || 'Details'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {this.state.rows.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatActivityTime(r.created_at)}</Table.Td>
                  <Table.Td>{actorName(r)}</Table.Td>
                  <Table.Td>
                    <Badge color={actionColor(r.action)} variant="light" radius="sm">
                      {actionLabel(s, r.action)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{localeLabel(r)}</Table.Td>
                  <Table.Td style={{ wordBreak: 'break-word' }}>{describeDetail(r, s)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {this.renderPagination()}
      </Box>
    );
  }

  render() {
    const s = Localization.strings;
    return (
      <Modal
        opened={!!this.props.open}
        onClose={this.props.onClose}
        title={
          <Group gap={8}>
            <IconHistory size={20} />
            <Text fw={600}>{s.SCHOOL_ACTIVITY_TITLE || 'School Activity Log'}</Text>
          </Group>
        }
        size="90%"
        radius="md"
        centered
        zIndex={2000}
        styles={{
          content: { height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
          body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
        }}
      >
        <Tabs value={this.state.tab} onChange={this.handleTabChange} keepMounted={false}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Tabs.List>
            <Tabs.Tab value={TAB_TEACHERS}>{s.ACTIVITY_TAB_TEACHERS || 'Teachers'}</Tabs.Tab>
            <Tabs.Tab value={TAB_LOGINS}>{s.ACTIVITY_TAB_LOGINS || 'Login activity'}</Tabs.Tab>
            <Tabs.Tab value={TAB_LOCALES}>{s.ACTIVITY_TAB_LOCALES || 'LOCALE activity'}</Tabs.Tab>
          </Tabs.List>
          <ScrollArea style={{ flex: 1, minHeight: 0 }} pt="sm">
            <Stack gap={0}>
              {this.state.tab === TAB_TEACHERS ? this.renderTeachers() : null}
              {this.state.tab === TAB_LOGINS ? this.renderLogins() : null}
              {this.state.tab === TAB_LOCALES ? this.renderLocales() : null}
            </Stack>
          </ScrollArea>
        </Tabs>
      </Modal>
    );
  }
}

export default SchoolActivityDialog;
